import { el, setChildren } from 'redom';

import application from 'application';

import * as basicEditorValueSerializer from 'basic-editor-value-serializer';

import './index.scss';

const UNPREVIEWABLE_BLOCK_TYPES = ['ht-form', 'member-calendar', 'source-code-region'];

function sanitizeNodes(nodes) {

  if (!nodes) {
    return nodes;
  }

  const result = [];

  for (let node of nodes) {
    if (UNPREVIEWABLE_BLOCK_TYPES.includes(node.type) === true) {
      result.push({
        ...node,
        type: 'paragraph',
        children: [{ text: 'Full preview not available' }],
      });
      continue;
    }

    if (node.children) {
      const sanitizedChildren = sanitizeNodes(node.children);
      result.push({ ...node, children: sanitizedChildren });
      continue;
    }

    result.push(node);
  }

  return result;

}

function buildSelectionSVG() {

  const ns = 'http://www.w3.org/2000/svg';

  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('width', '13');
  svg.setAttribute('height', '13');
  svg.setAttribute('viewBox', '0 0 13 13');
  svg.setAttribute('fill', 'none');

  const rect = document.createElementNS(ns, 'rect');
  rect.setAttribute('x', '1');
  rect.setAttribute('y', '1');
  rect.setAttribute('width', '11');
  rect.setAttribute('height', '11');
  rect.setAttribute('rx', '2');
  rect.setAttribute('stroke', 'currentColor');
  rect.setAttribute('stroke-width', '1.5');
  rect.setAttribute('stroke-dasharray', '3 2');
  svg.appendChild(rect);

  return svg;

}

function buildCheckSVG() {

  const ns = 'http://www.w3.org/2000/svg';

  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('width', '13');
  svg.setAttribute('height', '13');
  svg.setAttribute('viewBox', '0 0 13 13');
  svg.setAttribute('fill', 'none');

  const circle = document.createElementNS(ns, 'circle');
  circle.setAttribute('cx', '6.5');
  circle.setAttribute('cy', '6.5');
  circle.setAttribute('r', '5.5');
  circle.setAttribute('fill', 'currentColor');
  circle.setAttribute('opacity', '0.12');
  svg.appendChild(circle);

  const path = document.createElementNS(ns, 'path');
  path.setAttribute('d', 'M4 6.5l2 2 3.5-3.5');
  path.setAttribute('stroke', 'currentColor');
  path.setAttribute('stroke-width', '1.5');
  path.setAttribute('stroke-linecap', 'round');
  path.setAttribute('stroke-linejoin', 'round');
  svg.appendChild(path);

  return svg;

}

function buildChangeChips(summary) {

  const chips = [];

  if (summary.inserted > 0) {
    chips.push(el('.editor-selection-preview__chip.is-inserted', `+${summary.inserted}`));
  }

  if (summary.replaced > 0) {
    chips.push(el('.editor-selection-preview__chip.is-replaced', `~${summary.replaced}`));
  }

  if (summary.deleted > 0) {
    chips.push(el('.editor-selection-preview__chip.is-deleted', `-${summary.deleted}`));
  }

  return chips;

}

class EditorSelectionPreview {

  constructor(configuration = {}) {

    this.onClear = configuration.onClear || null;
    this.onShow = configuration.onShow || null;
    this.onHide = configuration.onHide || null;
    this.visible = false;

    this.maxHeight = configuration.maxHeight ?? 180;
    this.minScale = configuration.minScale ?? 0.35;

    this.resizeObserver = null;
    this.fitAnimationFrame = null;

    this.contentEl = el('.editor-selection-preview__content');

    this.viewportEl = el(
      '.editor-selection-preview__viewport',
      this.contentEl,
    );

    this.scaleBadgeEl = el('.editor-selection-preview__scale-badge');

    this.headerIconEl = el('.editor-selection-preview__header-icon');
    this.headerInfoEl = el('.editor-selection-preview__header-info');

    this.clearBtnEl = el(
      '.editor-selection-preview__clear-btn',
      '×', { onclick: this.handleClearClick.bind(this) },
    );

    this.headerEl = el(
      '.editor-selection-preview__header',
      this.headerIconEl,
      this.headerInfoEl,
      this.clearBtnEl,
    );

    this.el = el(
      '.editor-selection-preview',
      this.headerEl,
      this.viewportEl,
      this.scaleBadgeEl,
    );

    this.el.style.setProperty(
      '--editor-selection-preview-max-height',
      `${this.maxHeight}px`,
    );

    this.fitPreview = this.fitPreview.bind(this);
    this.scheduleFitPreview = this.scheduleFitPreview.bind(this);
    this.onContentResize = this.onContentResize.bind(this);

  }

  notifyShow() {
    if (this.visible === true) {
      return;
    }
    this.visible = true;
    if (this.onShow !== null) {
      this.onShow();
    }
  }

  notifyHide() {
    if (this.visible === false) {
      return;
    }
    this.visible = false;
    if (this.onHide !== null) {
      this.onHide();
    }
  }

  handleClearClick() {
    this.el.classList.remove('is-visible');
    if (this.onClear !== null) {
      this.onClear();
    }
    this.notifyHide();
  }

  onContentResize() {
    this.scheduleFitPreview();
  }

  clearPreview() {

    this.clearViewport();

    this.el.classList.remove(
      'is-visible',
      'is-edit-target',
      'is-change-applied',
    );

    this.notifyHide();

  }

  clearViewport() {

    this.contentEl.innerHTML = '';

    this.contentEl.style.transform = '';
    this.contentEl.style.width = '';
    this.viewportEl.style.height = '';
    this.scaleBadgeEl.hidden = true;

    if (this.resizeObserver !== null) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.fitAnimationFrame !== null) {
      cancelAnimationFrame(this.fitAnimationFrame);
      this.fitAnimationFrame = null;
    }

  }

  previewSelection(nodes) {

    try {

      if (!nodes || nodes.length === 0) {
        this.el.classList.remove('is-visible');
        this.notifyHide();
        return;
      }

      const blockCount = nodes.length;
      const label = blockCount === 1 ? '1 block selected' : `${blockCount} blocks selected`;

      setChildren(this.headerIconEl, [buildSelectionSVG()]);
      setChildren(this.headerInfoEl, [el('span.editor-selection-preview__label', label)]);

      this.el.classList.remove('is-change-applied');
      this.el.classList.add('is-edit-target', 'is-visible');

      this.notifyShow();
      this.renderNodes(nodes);

    } catch (exception) {

      application.constructor.logError('previewSelectionError', {
        nodes,
        exception
      });

    }

  }

  showChangeApplied(changeData) {

    const chips = buildChangeChips(changeData.summary);

    setChildren(this.headerIconEl, [buildCheckSVG()]);
    setChildren(this.headerInfoEl, chips);

    this.el.classList.remove('is-edit-target');
    this.el.classList.add('is-change-applied', 'is-visible');

    this.notifyShow();
    this.clearViewport();

  }

  renderNodes(nodes) {

    const sanitized = sanitizeNodes(nodes);

    this.contentEl.innerHTML = basicEditorValueSerializer.toNativeHTML(sanitized, { children: sanitized }, {});

    this.observeContentChanges();
    this.scheduleFitPreview();

  }

  observeContentChanges() {

    if (this.resizeObserver !== null) {
      this.resizeObserver.disconnect();
    }

    this.resizeObserver = new ResizeObserver(this.onContentResize);
    this.resizeObserver.observe(this.contentEl);

  }

  scheduleFitPreview() {

    if (this.fitAnimationFrame !== null) {
      cancelAnimationFrame(this.fitAnimationFrame);
    }

    this.fitAnimationFrame = requestAnimationFrame(function firstFrame() {
      this.fitAnimationFrame = requestAnimationFrame(function secondFrame() {
        this.fitAnimationFrame = null;
        this.fitPreview();
      }.bind(this));
    }.bind(this));

  }

  fitPreview() {

    const maxHeight = this.maxHeight;
    const minScale = this.minScale;

    this.contentEl.style.transform = 'none';
    this.contentEl.style.width = '100%';

    const naturalHeight = this.contentEl.scrollHeight;

    if (!naturalHeight) {
      return;
    }

    if (naturalHeight <= maxHeight) {
      this.viewportEl.style.height = `${naturalHeight}px`;
      this.contentEl.style.transform = 'none';
      this.contentEl.style.width = '100%';
      this.scaleBadgeEl.hidden = true;
      return;
    }

    this.viewportEl.style.height = `${maxHeight}px`;

    let scale = Math.max(minScale, maxHeight / naturalHeight);

    for (let i = 0; i < 3; i += 1) {
      this.contentEl.style.width = `${100 / scale}%`;
      this.contentEl.style.transform = `scale(${scale})`;

      const measuredHeight = this.contentEl.scrollHeight;
      const nextScale = Math.max(
        minScale,
        Math.min(1, maxHeight / measuredHeight),
      );

      if (Math.abs(nextScale - scale) < 0.01) {
        break;
      }

      scale = nextScale;
    }

    this.contentEl.style.width = `${100 / scale}%`;
    this.contentEl.style.transform = `scale(${scale})`;

    this.scaleBadgeEl.hidden = false;

    if (scale <= minScale) {
      this.scaleBadgeEl.textContent = 'Preview clipped';
    } else {
      this.scaleBadgeEl.textContent = 'Preview scaled';
    }

  }

}

export default EditorSelectionPreview;